import {
    getDatabase,
    ref,
    query,
    limitToLast,
    onValue,
    get,
} from "firebase/database";
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
    getUserByID,
    getAllUsersMeta,
    getCurrentUserDetails,
} from "../utils";
import AdminChatList from "./AdminChatList";
import AdminChatWindow from "./AdminChatWindow";

export default function AdminInbox() {
    const auth = useAuth();
    const currentUser = auth.currentUser;
    // Admin access is now enforced at the route level via RoleRoute in App.js
    const [chats, setChats] = useState([]); // Fetched via pagination (limitToLast)
    const [searchResults, setSearchResults] = useState([]); // Fetched via global search
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatParticipants, setChatParticipants] = useState({}); // Map chatId -> { names: "A & B" }
    const [userDetailsCache, setUserDetailsCache] = useState({}); // Map userId -> { firstName, lastName }
    const [chatLimit, setChatLimit] = useState(20);
    const [searchTerm, setSearchTerm] = useState("");
    const [isSearching, setIsSearching] = useState(false); // UI state
    const [loading, setLoading] = useState(false); // API state

    // 1. Pagination Effect (Loads recent chats) - NOW LOADS ALL
    useEffect(() => {
        const database = getDatabase();
        // Fetch ALL chats to ensure correct Client-Side Sorting by Timestamp
        // (Since keys are not chronological, limitToLast() server-side is incorrect for "Recent")
        const chatsRef = query(ref(database, "chats"));

        const unsubscribe = onValue(chatsRef, async (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const sortedChats = Object.keys(data)
                    .map((key) => ({
                        ...data[key],
                        chatId: key,
                    }))
                    .sort((a, b) => {
                        const lastMsgA = a.messages ? a.messages[a.messages.length - 1] : null;
                        const lastMsgB = b.messages ? b.messages[b.messages.length - 1] : null;
                        return (lastMsgB?.timestamp || 0) - (lastMsgA?.timestamp || 0);
                    });

                setChats(sortedChats);
                // Only fetch participants for the ones we are likely about to show (plus buffer) to save reads?
                // Actually, let's just fetch for the current limit to be safe and responsive.
                fetchParticipantsForChats(sortedChats.slice(0, 50));
            } else {
                setChats([]);
            }
        });

        return () => unsubscribe();
    }, []); // Run once on mount (live listener)

    // Effect to fetch more participants as chatLimit increases
    useEffect(() => {
        if (chats.length > 0) {
            const needed = chats.slice(0, chatLimit + 10);
            fetchParticipantsForChats(needed);
        }
    }, [chatLimit, chats]);

    // Helper to fetch participants (Memoized or defined outside to avoid stale closures if needed, but here simple function is fine)
    const fetchParticipantsForChats = async (chatList) => {
        const participants = {}; // Prepare local update to merge later
        const database = getDatabase(); // Ensure we have db ref if needed given context

        const promises = [];
        const newCache = {};

        for (const chat of chatList) {
            const p1Id = chat.senderId;
            const p2Id = chat.receiverId;

            if (p1Id && p2Id) {
                // Check if we already have this user details in cache or state
                // To avoid redundant fetches
                if (userDetailsCache[p1Id] && userDetailsCache[p2Id]) continue;

                const processChat = async () => {
                    try {
                        let user1 = userDetailsCache[p1Id];
                        if (!user1) {
                            user1 = await getUserByID(p1Id);
                            if (user1) newCache[p1Id] = user1;
                        }

                        let user2 = userDetailsCache[p2Id];
                        if (!user2) {
                            user2 = await getUserByID(p2Id);
                            if (user2) newCache[p2Id] = user2;
                        }

                        participants[chat.chatId] = {
                            names: `${user1?.firstName || "Unknown"} ${user1?.lastName || ""} & ${user2?.firstName || "Unknown"} ${user2?.lastName || ""}`,
                            p1: { id: p1Id, role: user1?.role, name: `${user1?.firstName || ""} ${user1?.lastName || ""}`.trim() || p1Id },
                            p2: { id: p2Id, role: user2?.role, name: `${user2?.firstName || ""} ${user2?.lastName || ""}`.trim() || p2Id }
                        };
                    } catch (e) {
                        console.error("Error fetching participants for chat", chat.chatId, e);
                    }
                };
                promises.push(processChat());
            }
        }

        if (promises.length > 0) {
            await Promise.all(promises);

            // Update states
            if (Object.keys(newCache).length > 0) {
                setUserDetailsCache(prev => ({ ...prev, ...newCache }));
            }
            if (Object.keys(participants).length > 0) {
                setChatParticipants(prev => ({ ...prev, ...participants }));
            }
        }
    };


    // 2. Search Effect (Debounced)
    useEffect(() => {
        if (searchTerm.length > 2) {
            setIsSearching(true);
            setLoading(true);

            const database = getDatabase();
            const timer = setTimeout(async () => {
                try {
                    // Fetch users directly from "users" node (source of truth)
                    const usersSnapshot = await get(ref(database, "users"));
                    const allUsersData = usersSnapshot.val();

                    const matchingUserIds = new Set();
                    const userMap = {};

                    if (allUsersData) {
                        Object.keys(allUsersData).forEach(uid => {
                            const user = allUsersData[uid];
                            userMap[uid] = { ...user, uid }; // Ensure UID is present

                            const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
                            // Expanded search: also check email if available
                            const email = (user.email || "").toLowerCase();

                            if (fullName.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase())) {
                                matchingUserIds.add(uid);
                            }
                        });
                    }

                    // For search, we can use the local `chats` state if it's populated (since we fetch ALL now),
                    // but to be safe and consistent with previous "Global Search" guarantee, let's filter `chats` state directly?
                    // Yes! We have ALL chats in `chats` state now. No need to fetch from DB again!
                    // Unless `chats` updates are slow? But `onValue` is fast.
                    // Let's optimize: Filter locally if `chats` has data.
                    // Actually, let's keep it simple: filter `chats` state.

                    let fetchedSearchResults = chats.filter(chat => {
                        const p1 = chat.senderId;
                        const p2 = chat.receiverId;
                        return matchingUserIds.has(p1) || matchingUserIds.has(p2);
                    });

                    // Sort results
                    fetchedSearchResults.sort((a, b) => {
                        const lastMsgA = a.messages ? a.messages[a.messages.length - 1] : null;
                        const lastMsgB = b.messages ? b.messages[b.messages.length - 1] : null;
                        return (lastMsgB?.timestamp || 0) - (lastMsgA?.timestamp || 0);
                    });

                    setSearchResults(fetchedSearchResults);

                    // Manually populate participants for search results from the `allUsersData` map we just built!
                    // This is MUCH faster than fetching one by one.
                    const participants = {};
                    fetchedSearchResults.forEach(chat => {
                        const p1Id = chat.senderId;
                        const p2Id = chat.receiverId;
                        const user1 = userMap[p1Id] || {};
                        const user2 = userMap[p2Id] || {};

                        participants[chat.chatId] = {
                            names: `${user1?.firstName || "Unknown"} ${user1?.lastName || ""} & ${user2?.firstName || "Unknown"} ${user2?.lastName || ""}`,
                            p1: { id: p1Id, role: user1?.role, name: `${user1?.firstName || ""} ${user1?.lastName || ""}`.trim() || p1Id },
                            p2: { id: p2Id, role: user2?.role, name: `${user2?.firstName || ""} ${user2?.lastName || ""}`.trim() || p2Id }
                        };
                    });
                    setChatParticipants(prev => ({ ...prev, ...participants }));

                } catch (error) {
                    console.error("Global search failed:", error);
                } finally {
                    setLoading(false);
                }
            }, 500);

            return () => clearTimeout(timer);
        } else {
            setIsSearching(false);
            setSearchResults([]);
            setLoading(false);
        }
    }, [searchTerm, chats]); // Depend on chats too

    const handleLoadMore = () => {
        if (!isSearching) {
            setChatLimit((prev) => prev + 20);
        }
    };

    const handleSelectChat = (chat) => {
        setSelectedChat(chat);
    };

    // 3. Merged Display Logic
    const displayedChats = useMemo(() => {
        if (!searchTerm) {
            // No search: show loaded chats (Sliced for pagination/rendering performance)
            return chats.slice(0, chatLimit);
        }

        // Filter local available chats by name (using already loaded participants)
        const localMatches = chats.filter((chat) => {
            const participantData = chatParticipants[chat.chatId];
            const names = participantData?.names || "";
            return names.toLowerCase().includes(searchTerm.toLowerCase());
        });

        if (!isSearching) {
            return localMatches;
        }

        // Merge localMatches + searchResults (Global)
        // Use Map to deduplicate by chatId
        const merged = new Map();
        localMatches.forEach(c => merged.set(c.chatId, c));
        searchResults.forEach(c => merged.set(c.chatId, c));

        const result = Array.from(merged.values());

        // Sort merged results
        result.sort((a, b) => {
            const lastMsgA = a.messages ? a.messages[a.messages.length - 1] : null;
            const lastMsgB = b.messages ? b.messages[b.messages.length - 1] : null;
            return (lastMsgB?.timestamp || 0) - (lastMsgA?.timestamp || 0);
        });

        return result;

    }, [chats, searchResults, searchTerm, chatParticipants, isSearching, chatLimit]);


    return (
        <div className="mailbox">
            <div className="row h-100">
                <AdminChatList
                    chats={displayedChats}
                    selectedChat={selectedChat}
                    onSelectChat={handleSelectChat}
                    participants={chatParticipants}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    onLoadMore={handleLoadMore}
                    isLoading={loading}
                />

                <AdminChatWindow
                    selectedChat={selectedChat}
                    currentUser={currentUser}
                    participants={chatParticipants}
                    userDetailsCache={userDetailsCache}
                />
            </div>
        </div>
    );
}
